/**
 * Story detail tab bar - PRD / Tech Design / Tasks / custom doc tabs
 * Story 199: View story details with PRD and design
 * Story 232: Deleted sections history button
 * Story 297: Dynamic tabs for generic document types
 * Story 356: Web event instrumentation
 * Story 514: Project settings cog pinned to the far right
 */

import { useCallback } from 'react';
import { track } from '../../lib/analytics';
import './StoryTabs.css';

/** Fixed tab identifiers */
export type FixedTabId = 'prd' | 'design' | 'tasks' | 'updates';

/**
 * Tab identifier - either a fixed tab or a dynamic doc tab.
 * Story 297: Custom doc tabs use `doc:<slug>` prefix (e.g., `doc:mcp-research`).
 */
export type TabId = FixedTabId | `doc:${string}`;

/** Tab prefix for dynamic document tabs */
export const DOC_TAB_PREFIX = 'doc:' as const;

interface Tab {
  id: TabId;
  label: string;
}

const FIXED_TABS: Tab[] = [
  { id: 'prd', label: 'PRD' },
  { id: 'design', label: 'Tech Design' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'updates', label: 'Updates' },
];

/** A custom document tab discovered from the API */
export interface CustomTab {
  docType: string;
  label: string;
}

interface StoryTabsProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  /** Story 356: Story ID for analytics tracking */
  storyId?: string;
  /** Story 297: Dynamic document tabs from useStoryDocuments */
  customTabs?: CustomTab[];
  /** Story 297: Called when + button is clicked to add a new document tab */
  onAddTab?: () => void;
  /** Number of deleted sections (Story 232) */
  deletedCount?: number;
  /** Called when deleted sections button is clicked (Story 232) */
  onViewDeleted?: () => void;
  /** Story 514: when given, the Project settings cog is rendered at the far right */
}

export function StoryTabs({
  activeTab,
  onTabChange,
  storyId,
  customTabs = [],
  onAddTab,
  deletedCount = 0,
  onViewDeleted,
}: StoryTabsProps) {
  // Story 356: Wrap onTabChange to track tab switches
  const handleTabChange = useCallback((tab: TabId) => {
    // Only track if switching to a different tab
    if (tab !== activeTab) {
      track({
        name: 'tab_switched',
        properties: { story_id: storyId, tab },
      });
    }
    onTabChange(tab);
  }, [activeTab, onTabChange, storyId]);

  return (
    <div className="story-tabs">
      {FIXED_TABS.map((tab) => (
        <button
          key={tab.id}
          className={`story-tabs__tab ${activeTab === tab.id ? 'story-tabs__tab--active' : ''}`}
          onClick={() => handleTabChange(tab.id)}
          type="button"
        >
          {tab.label}
        </button>
      ))}

      {/* Story 297: Dynamic custom document tabs */}
      {customTabs.map((custom) => {
        const tabId: TabId = `${DOC_TAB_PREFIX}${custom.docType}`;
        return (
          <button
            key={tabId}
            className={`story-tabs__tab ${activeTab === tabId ? 'story-tabs__tab--active' : ''}`}
            onClick={() => handleTabChange(tabId)}
            type="button"
          >
            {custom.label}
          </button>
        );
      })}

      {/* Story 297: Add document tab button */}
      {onAddTab && (
        <button
          className="story-tabs__add-btn"
          onClick={onAddTab}
          type="button"
          title="Add document tab"
          aria-label="Add document tab"
        >
          <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
            <path d="M7.75 2a.75.75 0 0 1 .75.75V7h4.25a.75.75 0 0 1 0 1.5H8.5v4.25a.75.75 0 0 1-1.5 0V8.5H2.75a.75.75 0 0 1 0-1.5H7V2.75A.75.75 0 0 1 7.75 2z" />
          </svg>
        </button>
      )}

      {/* Story 514: everything after the spacer is pinned to the far right */}
      <span className="story-tabs__spacer" aria-hidden="true" />

      {/* Story 232: Deleted sections history button - only show when there are deleted sections */}
      {deletedCount > 0 && onViewDeleted && (
        <button
          className="story-tabs__deleted-btn"
          onClick={onViewDeleted}
          type="button"
          title={`View ${deletedCount} deleted section${deletedCount > 1 ? 's' : ''}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </button>
      )}

    </div>
  );
}
